// RECALL-ROLES-ONBOARDING (design 2026-10-31 recall full integration, §4.6, §7.1): the people side
// of memory. A seat's role and its handover to the next holder, the profile drafted from a seat
// ("Is this you?"), the member card (role, what they are accountable for, who to ask about what),
// the onboarding the Chief of Staff DE runs in chat, and a space's charter.
//
// Roles, successions, profiles, onboarding and charters need the person assertion on Recall's
// side, which Recall does not list yet. Every act here is therefore recorded in CompanyOS first
// and queued; `doors` on each answer says whether the memory service takes it now (`live`), or the
// act waits (`waiting`, shown as "Waiting on memory service"), or memory is not on for the
// company (`off`). Nothing here fails because of that.
import { z } from "zod";
import {
  RecallCharterSource,
  RecallCharterState,
  RecallCharterSubject,
  RecallOnboardingDepth,
  RecallOnboardingState,
  RecallRoleState,
  RecallSuccessionState,
} from "../enums";
import { Timestamp, Uuid } from "../primitives";
import { CompanyItemParams, CompanyParams } from "./common";
import { defineRoute } from "./route";

const base = "/v1/companies/{co}";
const memory = { feature: "memory", tag: "memory" } as const;

/**
 * Whether the memory service takes these acts now. `live`: sent as they happen; `waiting`: kept
 * here and sent when it does (`label` is the words to show); `off`: memory is not on.
 */
export const MemoryDoors = z
  .object({
    state: z.enum(["live", "waiting", "off"]),
    label: z.string().nullable(),
  })
  .meta({ id: "MemoryDoors" });
export type MemoryDoors = z.infer<typeof MemoryDoors>;

/** The profile CompanyOS drafted from a person's seat, for them to accept or edit. */
export const ProfileDraft = z
  .object({
    id: Uuid,
    role: z.string(),
    kpis: z.array(z.string()),
    skills: z.array(z.string()),
    text: z.string(),
    expires_at: Timestamp,
  })
  .meta({ id: "ProfileDraft" });
export type ProfileDraft = z.infer<typeof ProfileDraft>;

/** The person's own edits when they accept ("Is this you?" → yes, with changes). */
export const ProfileAccept = z
  .strictObject({
    text: z.string().trim().min(1).max(2000).optional(),
    role: z.string().trim().min(1).max(200).optional(),
    kpis: z.array(z.string().trim().min(1).max(120)).max(12).optional(),
    skills: z.array(z.string().trim().min(1).max(120)).max(12).optional(),
  })
  .meta({ id: "ProfileAccept" });

/**
 * A member's card: the seat they hold, what they are accountable for (their own and an admin's
 * view), the topics they are the one to ask about, and for themself the profile draft waiting for
 * a yes.
 */
export const MemberCard = z
  .object({
    membership_id: Uuid,
    name: z.string(),
    is_self: z.boolean(),
    seats: z.array(z.object({ seat_id: Uuid, title: z.string() })),
    /** Their KPIs: shown to the person and to admins, not to colleagues. */
    kpis: z.array(z.string()),
    skills: z.array(z.string()),
    /** Who-knows: the topics to ask this person about. */
    knows_about: z.array(z.string()),
    /** Their own draft, only to them. */
    profile: ProfileDraft.nullable(),
    /** Acts of theirs still waiting for the memory service (to them and admins). */
    waiting: z.int().min(0),
    doors: MemoryDoors,
    /** Behind (i): what the card is made of, in a line. */
    info: z.string(),
  })
  .meta({ id: "MemberCard" });
export type MemberCard = z.infer<typeof MemberCard>;

/** A seat's memory handed to its next holder: the card "Take over this seat's memory". */
export const SeatSuccession = z
  .object({
    id: Uuid,
    seat_id: Uuid,
    seat_title: z.string(),
    state: RecallSuccessionState,
    from_name: z.string().nullable(),
    to_membership_id: Uuid,
    to_name: z.string(),
    detail: z.string().nullable(),
    can_answer: z.boolean(),
    can_settle: z.boolean(),
    created_at: Timestamp,
  })
  .meta({ id: "SeatSuccession" });
export type SeatSuccession = z.infer<typeof SeatSuccession>;

export const SuccessionList = z
  .object({ items: z.array(SeatSuccession), doors: MemoryDoors })
  .meta({ id: "SuccessionList" });
export type SuccessionList = z.infer<typeof SuccessionList>;

export const SuccessionScope = z.object({ scope: z.enum(["mine", "all"]).optional() });

export const SuccessionAnswer = z
  .strictObject({ answer: z.enum(["accept", "decline"]) })
  .meta({ id: "SuccessionAnswer" });
export const SuccessionSettle = z
  .strictObject({ how: z.enum(["cancel", "apply"]) })
  .meta({ id: "SuccessionSettle" });

export const ActingParams = CompanyItemParams.extend({ membership_id: Uuid });

/** A seat's role in memory, as the admin sees it. */
export const SeatRole = z
  .object({
    seat_id: Uuid,
    title: z.string(),
    state: RecallRoleState,
    detail: z.string().nullable(),
  })
  .meta({ id: "SeatRole" });
export const RolesView = z
  .object({ items: z.array(SeatRole), waiting: z.int().min(0), doors: MemoryDoors })
  .meta({ id: "RolesView" });
export type RolesView = z.infer<typeof RolesView>;

/** What closing a seat does to its role in memory, shown to the admin first. */
export const RolePreview = z
  .object({
    seat_id: Uuid,
    title: z.string(),
    holders: z.array(z.string()),
    open_successions: z.int().min(0),
    waiting_acts: z.int().min(0),
    /** Memory's own count of records the retire would affect, when it answered. */
    records: z.int().min(0).nullable(),
    doors: MemoryDoors,
  })
  .meta({ id: "RolePreview" });

/** A person's onboarding with the Chief of Staff DE. */
export const OnboardingView = z
  .object({
    state: z.enum(["none", ...RecallOnboardingState.options]),
    depth: RecallOnboardingDepth.nullable(),
    progress: z.object({ done: z.int().min(0), total: z.int().min(0) }),
    /** Only a member's quick card can be put aside. */
    can_dismiss: z.boolean(),
    /** The chat with the Chief of Staff DE the questions are asked in. */
    thread_id: Uuid.nullable(),
    /** Why nothing started (no Chief of Staff yet), in words. */
    note: z.string().nullable(),
    doors: MemoryDoors,
  })
  .meta({ id: "OnboardingView" });
export type OnboardingView = z.infer<typeof OnboardingView>;

/** A charter: what a space is for and how it works. */
export const CharterView = z
  .object({
    subject_kind: RecallCharterSubject,
    subject_id: Uuid,
    text: z.string(),
    source: RecallCharterSource.nullable(),
    state: z.enum(["none", ...RecallCharterState.options]),
    detail: z.string().nullable(),
    /** A starting text for the modal: the Vision for the company, the description for a space. */
    suggested: z.string().nullable(),
    can_edit: z.boolean(),
    doors: MemoryDoors,
  })
  .meta({ id: "CharterView" });
export type CharterView = z.infer<typeof CharterView>;

export const CharterParams = CompanyParams.extend({
  kind: RecallCharterSubject,
  id: Uuid,
});
export const CharterSet = z
  .strictObject({ text: z.string().trim().min(1).max(2000) })
  .meta({ id: "CharterSet" });

export const memoryRolesRoutes = {
  "memoryPeople.card": defineRoute({
    ...memory,
    id: "memoryPeople.card",
    method: "get",
    path: `${base}/memory/people/{id}/card`,
    summary: "A member's card: seat, what they are accountable for, who to ask about what",
    description:
      "RECALL-ROLES-ONBOARDING. `id` is the member's membership. KPIs and a waiting profile draft " +
      "show to the person and to managers only. Who-knows topics come from the seat (its " +
      "responsibilities and skills) and work whether or not the memory service takes them yet.",
    access: "member",
    params: CompanyItemParams,
    response: { status: 200, schema: MemberCard },
    errors: [],
    emits: [],
  }),
  "memoryPeople.profileAccept": defineRoute({
    ...memory,
    id: "memoryPeople.profileAccept",
    method: "post",
    path: `${base}/me/profile/draft/accept`,
    summary: "Yes, this is me: accept the profile drafted from my seat (with my edits)",
    description:
      "Sent to memory as the person (queued while the memory service does not take it yet).",
    access: "member",
    params: CompanyParams,
    body: ProfileAccept,
    response: { status: 200, schema: MemberCard },
    errors: ["not_found", "validation_failed"],
    emits: ["memory.profile_decided"],
  }),
  "memoryPeople.profileDiscard": defineRoute({
    ...memory,
    id: "memoryPeople.profileDiscard",
    method: "post",
    path: `${base}/me/profile/draft/discard`,
    summary: "No: throw the drafted profile away",
    access: "member",
    params: CompanyParams,
    response: { status: 200, schema: MemberCard },
    errors: ["not_found"],
    emits: ["memory.profile_decided"],
  }),
  "memoryPeople.successions": defineRoute({
    ...memory,
    id: "memoryPeople.successions",
    method: "get",
    path: `${base}/memory/successions`,
    summary: "Seat memories handed to the caller to take over (managers: all handovers)",
    access: "member",
    params: CompanyParams,
    query: SuccessionScope,
    response: { status: 200, schema: SuccessionList },
    errors: [],
    emits: [],
  }),
  "memoryPeople.successionAnswer": defineRoute({
    ...memory,
    id: "memoryPeople.successionAnswer",
    method: "post",
    path: `${base}/memory/successions/{id}/answer`,
    summary: "Take over this seat's memory, or not",
    description: "Only the new holder answers. Sent to memory as that person.",
    access: "member",
    params: CompanyItemParams,
    body: SuccessionAnswer,
    response: { status: 200, schema: SeatSuccession },
    errors: ["not_found", "conflict"],
    emits: ["memory.succession_answered", "memory.acting_ended"],
  }),
  "memoryPeople.successionSettle": defineRoute({
    ...memory,
    id: "memoryPeople.successionSettle",
    method: "post",
    path: `${base}/memory/successions/{id}/settle`,
    summary: "Cancel a seat's memory handover, or apply it to a vacant seat",
    access: "manager",
    params: CompanyItemParams,
    body: SuccessionSettle,
    response: { status: 200, schema: SeatSuccession },
    errors: ["not_found", "conflict"],
    emits: ["memory.succession_settled", "memory.acting_ended"],
  }),
  "memoryPeople.roles": defineRoute({
    ...memory,
    id: "memoryPeople.roles",
    method: "get",
    path: `${base}/memory/roles`,
    summary: "The seats as memory roles, and what still waits for the memory service",
    access: "manager",
    params: CompanyParams,
    response: { status: 200, schema: RolesView },
    errors: [],
    emits: [],
  }),
  "memoryPeople.retirePreview": defineRoute({
    ...memory,
    id: "memoryPeople.retirePreview",
    method: "get",
    path: `${base}/memory/roles/{id}/retire-preview`,
    summary: "What closing a seat does to its role in memory",
    description: "`id` is the seat. Shown to the admin before the seat is closed.",
    access: "manager",
    params: CompanyItemParams,
    response: { status: 200, schema: RolePreview },
    errors: ["not_found"],
    emits: [],
  }),
  "memoryPeople.actingEnd": defineRoute({
    ...memory,
    id: "memoryPeople.actingEnd",
    method: "post",
    path: `${base}/memory/roles/{id}/acting/{membership_id}/end`,
    summary: "End an acting holder's cover over a seat's memory",
    description: "`id` is the seat. Also runs by itself when an assignment on the seat ends.",
    access: "manager",
    params: ActingParams,
    response: { status: 200, schema: RolesView },
    errors: ["not_found"],
    emits: ["memory.acting_ended"],
  }),
  "memoryPeople.onboarding": defineRoute({
    ...memory,
    id: "memoryPeople.onboarding",
    method: "get",
    path: `${base}/me/onboarding`,
    summary: "Where the caller's onboarding with the Chief of Staff DE stands",
    description:
      "The owner is offered the deep flow, admins the medium one, members one optional quick " +
      "card. The questions are asked in chat; the card only shows progress.",
    access: "member",
    params: CompanyParams,
    response: { status: 200, schema: OnboardingView },
    errors: [],
    emits: [],
  }),
  "memoryPeople.onboardingStart": defineRoute({
    ...memory,
    id: "memoryPeople.onboardingStart",
    method: "post",
    path: `${base}/me/onboarding/start`,
    summary: "Start the onboarding: the Chief of Staff DE asks the first question in chat",
    access: "member",
    params: CompanyParams,
    response: { status: 200, schema: OnboardingView },
    errors: [],
    emits: ["memory.onboarding_changed", "message.created"],
  }),
  "memoryPeople.onboardingDismiss": defineRoute({
    ...memory,
    id: "memoryPeople.onboardingDismiss",
    method: "post",
    path: `${base}/me/onboarding/dismiss`,
    summary: "Not now: close the optional onboarding card",
    description:
      "Members only; the owner's and admins' flows cannot be put aside (nothing changes and " +
      "`can_dismiss` says so).",
    access: "member",
    params: CompanyParams,
    response: { status: 200, schema: OnboardingView },
    errors: [],
    emits: ["memory.onboarding_changed"],
  }),
  "memoryPeople.charter": defineRoute({
    ...memory,
    id: "memoryPeople.charter",
    method: "get",
    path: `${base}/memory/charters/{kind}/{id}`,
    summary: "A space's charter (or the company space's), with a text to start from",
    access: "member",
    params: CharterParams,
    response: { status: 200, schema: CharterView },
    errors: ["not_found"],
    emits: [],
  }),
  "memoryPeople.charterSet": defineRoute({
    ...memory,
    id: "memoryPeople.charterSet",
    method: "put",
    path: `${base}/memory/charters/{kind}/{id}`,
    summary: "Write a space's charter",
    description:
      "Managers write the company's; a space's manager writes the space's. Sent to memory as " +
      "an admin (queued while the memory service does not take it yet).",
    access: "member",
    params: CharterParams,
    body: CharterSet,
    response: { status: 200, schema: CharterView },
    errors: ["not_found", "validation_failed"],
    emits: ["memory.charter_set"],
  }),
  "memoryPeople.charterFromVision": defineRoute({
    ...memory,
    id: "memoryPeople.charterFromVision",
    method: "post",
    path: `${base}/memory/charters/company/from-vision`,
    summary: "Use the Vision as the company space's charter",
    description: "An empty Vision changes nothing.",
    access: "manager",
    params: CompanyParams,
    response: { status: 200, schema: CharterView },
    errors: [],
    emits: ["memory.charter_set"],
  }),
} as const;
